import { PESTS_COVERED_STATUS } from '@ps/common-features/constants/pests'
import { Logger } from '@ps/common-features/sentry/loggerService'
import { REQUEST_STATUS } from 'src/api/constants/request'
import { WORK_ORDER_SCHEDULE_TYPES, WORK_ORDER_STATUS } from 'src/api/constants/workOrder'
import { groupRequestStatus } from 'src/api/utils/groupRequestStatus'
import z from 'zod'

export const StatusSchema = z.object({
  slug: z.string(),
  name: z.string(),
})

export const ServiceRequestStatus = z.nativeEnum(REQUEST_STATUS)
export const WorkOrderStatus = z.nativeEnum(WORK_ORDER_STATUS)

export const PEST_COVERAGE_KEYS = {
  ...PESTS_COVERED_STATUS,
  unknown: 'unknown',
} as const

export const PestStatusSchema = StatusSchema.extend({
  slug: z.nativeEnum(PEST_COVERAGE_KEYS).catch((slug) => {
    Logger.error('Invalid pest status', { context: { slug: slug.input } })

    return 'unknown'
  }),
})

export const RequestStatusSchema = StatusSchema.extend({
  slug: z.nativeEnum(REQUEST_STATUS).catch((slug) => {
    Logger.error('Invalid request status', { context: { slug: slug.input } })

    return 'unknown'
  }),
})

export const WorkOrderStatusSchema = StatusSchema.extend({
  slug: z.nativeEnum(WORK_ORDER_STATUS).catch((slug) => {
    Logger.error('Invalid work order status', { context: { slug: slug.input } })

    return 'unknown'
  }),
})

export const WorkOrderSchedulingRawSchema = z.discriminatedUnion('schedule_type', [
  z.object({
    schedule_type: z.literal(WORK_ORDER_SCHEDULE_TYPES.UNSCHEDULED),
    first_service_date: z.string().nullish(),
    last_service_date: z.string().nullish(),
  }),
  z.object({
    schedule_type: z.enum([
      WORK_ORDER_SCHEDULE_TYPES.SINGLE_DAY,
      WORK_ORDER_SCHEDULE_TYPES.MULTI_DAY,
    ]),
    first_service_date: z.string(),
    last_service_date: z.string().nullish(),
  }),
])

export const WorkOrderSchedulingSchema = WorkOrderSchedulingRawSchema.transform(
  ({ schedule_type, first_service_date, last_service_date }) => ({
    firstServiceDate: first_service_date,
    lastServiceDate: last_service_date,
    scheduleType: schedule_type,
  }),
)

export const WorkOrderProviderSchema = z.object({
  name: z.string(),
  phone: z.string(),
})

export const PestSchema = z.object({
  image: z.string().nullish(),
  name: z.string(),
  status: PestStatusSchema,
})

export const WorkOrderRawSchema = z.object({
  id: z.number().int(),
  status: StatusSchema,
  pcr_status: StatusSchema,
  scheduling: WorkOrderSchedulingRawSchema,
  provider: WorkOrderProviderSchema,
  pests: z.array(PestSchema),
})

export const WorkOrderSchema = WorkOrderRawSchema.transform(({ pcr_status, status, ...rest }) => ({
  ...rest,
  status: WorkOrderStatusSchema.parse(status),
  pcrStatus: pcr_status,
  scheduling: WorkOrderSchedulingSchema.parse(rest.scheduling),
}))

export const TrackingDataRawSchema = z.object({
  deny_reason: z.string().nullish(),
  request_number: z.number().int(),
  request_pests: z.array(PestSchema),
  request_status: StatusSchema,
  submitted_at: z.string(),
  work_orders: z.array(WorkOrderRawSchema),
})

export const TrackingDataSchema = TrackingDataRawSchema.transform(
  ({ request_number, request_pests, request_status, submitted_at, work_orders, deny_reason }) => {
    const parsedWorkOrders = work_orders.map((workOrder) => WorkOrderSchema.parse(workOrder))
    const parsedRequestStatus = RequestStatusSchema.parse(request_status)
    const workOrderPestsByName = parsedWorkOrders.reduceRight<
      Record<
        string,
        {
          workOrder: (typeof parsedWorkOrders)[number]
          pest: (typeof parsedWorkOrders)[number]['pests'][number]
        }
      >
    >((lookup, workOrder) => {
      workOrder.pests.forEach((pest) => {
        if (lookup[pest.name] == null) {
          lookup[pest.name] = { workOrder, pest }
        }
      })

      return lookup
    }, {})
    const activeWorkOrder = parsedWorkOrders.at(-1)
    // If this request is archived but its work order belongs to a different active request,
    // use the active request's status, exposed as the work order's PCR status, here instead
    const effectiveRequestStatus =
      parsedRequestStatus.slug === REQUEST_STATUS.ARCHIVED && activeWorkOrder != null
        ? RequestStatusSchema.parse(activeWorkOrder.pcrStatus)
        : parsedRequestStatus

    return {
      denyReason: deny_reason,
      requestNumber: request_number,
      requestPests: request_pests,
      requestStatus: effectiveRequestStatus,
      submittedAt: submitted_at,
      workOrders: parsedWorkOrders,
      parsedFields: {
        groupedRequestStatus: groupRequestStatus(effectiveRequestStatus.slug),
        // API: Sorted by created_at asc (oldest to newest using unix timestamp),
        // so the last work order is the most recent one
        activeWorkOrder,
        workOrderPestsByName,
      },
    }
  },
)
